import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowRight, Apple, LayoutGrid, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import ProductCard from "../../components/ProductCard";
import {
  getBestSellers,
  // getBestDeals,
  // getBrandsList,
  getProductsByFilters,
  getProducts,
} from "../../api/productApi";
import CarouselSlider from "../../components/CarouselSlider";
import BestSellers from "./components/BestSellers";
import BestDeals from "./components/BestDeals";
import WhyChooseUs from "./components/WhyChooseUs";
import WatchAndShop from "./components/WatchAndShop";
import BrandLogos from "./components/BrandLogos";
import BlogSection from "./components/BlogSection";
import FaqSection from "./components/FaqSection";
import TestimonialSection from "./components/TestimonialSection";
import HotDealSection from "./components/HotDealSection";
import {
  ProductCarouselShimmer,
  BrandShimmer,
} from "../../components/ShimmerEffect";
import { useGlobalData } from "../../contexts/GlobalDataContext";

// ─── Home Page ────────────────────────────────────────────────────────────────
const Home = ({ onCartUpdate }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("search") || "";

  const appleScrollRef = useRef(null);
  const allScrollRef = useRef(null);

  const [canScrollAppleLeft, setCanScrollAppleLeft] = useState(false);
  const [canScrollAppleRight, setCanScrollAppleRight] = useState(true);
  const [canScrollAllLeft, setCanScrollAllLeft] = useState(false);
  const [canScrollAllRight, setCanScrollAllRight] = useState(true);

  const checkScrollApple = () => {
    if (appleScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = appleScrollRef.current;
      setCanScrollAppleLeft(scrollLeft > 0);
      setCanScrollAppleRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }
  };

  const checkScrollAll = () => {
    if (allScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = allScrollRef.current;
      setCanScrollAllLeft(scrollLeft > 0);
      setCanScrollAllRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }
  };

  const scrollContainer = (ref, direction) => {
    if (ref.current) {
      const { scrollLeft, clientWidth } = ref.current;
      const scrollTo = direction === "left" ? scrollLeft - clientWidth : scrollLeft + clientWidth;
      ref.current.scrollTo({ left: scrollTo, behavior: "smooth" });
    }
  };

  const [bestSellers, setBestSellers] = useState([]);
  const [bestSellersLoading, setBestSellersLoading] = useState(true);
  const { brands, brandsLoading, bestDeals, bestDealsVisible, bestDealsLoading } = useGlobalData();
  const [appleProducts, setAppleProducts] = useState([]);
  const [appleLoading, setAppleLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [allProductsLoading, setAllProductsLoading] = useState(true);

  const filterProducts = (products) => {
    if (!searchQuery || searchQuery.trim().length === 0) return products;
    const query = searchQuery.toLowerCase().trim();
    return products.filter(
      (product) =>
        product.name?.toLowerCase().includes(query) ||
        product.brand?.toLowerCase().includes(query) ||
        product.category?.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query),
    );
  };

  const filteredBestSellers = useMemo(() => filterProducts(bestSellers), [bestSellers, searchQuery]);
  const filteredAppleProducts = useMemo(() => filterProducts(appleProducts), [appleProducts, searchQuery]);
  const filteredAllProducts = useMemo(() => filterProducts(allProducts), [allProducts, searchQuery]);

  useEffect(() => {
    checkScrollApple();
    checkScrollAll();
    window.addEventListener("resize", checkScrollApple);
    window.addEventListener("resize", checkScrollAll);
    return () => {
      window.removeEventListener("resize", checkScrollApple);
      window.removeEventListener("resize", checkScrollAll);
    };
  }, [filteredAppleProducts, filteredAllProducts]);

  useEffect(() => {
    const fetchBestSellers = async () => {
      try {
        setBestSellersLoading(true);
        const response = await getBestSellers({ limit: 5 });
        if (response.success && response.data?.products) setBestSellers(response.data.products);
      } catch (error) {
        console.error("Error fetching best sellers:", error);
        setBestSellers([]);
      } finally {
        setBestSellersLoading(false);
      }
    };
    fetchBestSellers();
  }, []);

  useEffect(() => {
    const fetchAppleProducts = async () => {
      try {
        setAppleLoading(true);
        const response = await getProductsByFilters({ brand: "Apple", limit: 5 });
        if (response.success && response.data?.products) setAppleProducts(response.data.products);
      } catch (error) {
        console.error("Error fetching Apple products:", error);
        setAppleProducts([]);
      } finally {
        setAppleLoading(false);
      }
    };
    fetchAppleProducts();
  }, []);

  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        setAllProductsLoading(true);
        const response = await getProducts({ limit: 10 });
        if (response.success && response.data?.products) setAllProducts(response.data.products);
      } catch (error) {
        console.error("Error fetching all products:", error);
        setAllProducts([]);
      } finally {
        setAllProductsLoading(false);
      }
    };
    fetchAllProducts();
  }, []);

  const handleAddToCart = () => {
    if (onCartUpdate) onCartUpdate();
  };

  return (
    <div className="overflow-x-hidden">
      {/* Carousel Slider Banner */}
      <CarouselSlider />

      {/* Scrolling Banner */}
      <div className="bg-white text-black py-3 overflow-hidden border-b border-gray-200">
        <div className="animate-scroll-left flex space-x-12 whitespace-nowrap">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="flex items-center space-x-12 capitalize text-gray-800"
              style={{ fontFamily: "'Gilroy-Bold', 'Gilroy-Regular', sans-serif", fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.08em' }}
            >
              <span>&bull; Dell</span>
              <span>&bull; HP</span>
              <span>&bull; Lenovo</span>
              <span>&bull; Asus</span>
              <span>&bull; Acer</span>
              <span>&bull; Microsoft</span>
              <span>&bull; ThinkPad</span>
              <span>&bull; MSI</span>
              <span>&bull; Samsung</span>
              <span>&bull; Apple</span>
              <span>&bull; Sony</span>
              <span>&bull; Galaxy</span>
              <span>&bull; Razer</span>
            </div>
          ))}
        </div>
      </div>

     

      {/* Best Sellers */}
      <BestSellers
        bestSellersLoading={bestSellersLoading}
        filteredBestSellers={filteredBestSellers}
        searchQuery={searchQuery}
        handleAddToCart={handleAddToCart}
      />

      {/* Apple Products */}
      {(appleLoading || filteredAppleProducts.length > 0) && (
        <section className="py-14 bg-gray-50 dark:bg-gray-900">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-900 dark:bg-gray-700 text-white">
                  <Apple size={20} />
                </span>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
                    Apple Products
                  </h2>
                  <p className="text-xs text-gray-400 dark:text-gray-500 font-medium">MacBooks &amp; Apple laptops</p>
                </div>
              </div>
              <Link
                to="/all-products?brand=Apple"
                className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                View All <ArrowRight size={15} />
              </Link>
            </div>

            {appleLoading ? (
              <ProductCarouselShimmer count={5} />
            ) : (
              <div className="relative group">
                <button
                  onClick={() => scrollContainer(appleScrollRef, "left")}
                  className={`absolute left-[-20px] top-[45%] -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-12 h-12 rounded-full shadow-lg border text-gray-700 dark:text-gray-200 transition-all hover:scale-105 ${canScrollAppleLeft ? "opacity-0 group-hover:opacity-100 cursor-pointer" : "opacity-0 pointer-events-none"}`}
                  style={{ background: "var(--surface-elevated)", borderColor: "var(--surface-border)" }}
                  aria-label="Scroll left"
                >
                  <ChevronLeft size={24} />
                </button>

                <div
                  ref={appleScrollRef}
                  onScroll={checkScrollApple}
                  className="flex overflow-x-auto pb-6 gap-5 snap-x scrollbar-hide scroll-smooth"
                >
                  {filteredAppleProducts.map((product) => (
                    <div key={product._id || product.id} className="w-full sm:w-[280px] lg:w-[290px] shrink-0 snap-center">
                      <ProductCard product={product} onAddToCart={handleAddToCart} />
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => scrollContainer(appleScrollRef, "right")}
                  className={`absolute right-[-20px] top-[45%] -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-12 h-12 rounded-full shadow-lg border text-gray-700 dark:text-gray-200 transition-all hover:scale-105 ${canScrollAppleRight ? "opacity-0 group-hover:opacity-100 cursor-pointer" : "opacity-0 pointer-events-none"}`}
                  style={{ background: "var(--surface-elevated)", borderColor: "var(--surface-border)" }}
                  aria-label="Scroll right"
                >
                  <ChevronRight size={24} />
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      <BrandLogos brands={brands} loading={brandsLoading} />

      {/* All Products */}
      <section className="py-14 bg-white dark:bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
                <LayoutGrid size={20} />
              </span>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
                  All Products
                </h2>
                <p className="text-xs text-gray-400 dark:text-gray-500 font-medium">Browse our full laptop collection</p>
              </div>
            </div>
            <Link
              to="/all-products"
              className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              View All <ArrowRight size={15} />
            </Link>
          </div>

          {allProductsLoading ? (
            <ProductCarouselShimmer count={5} />
          ) : filteredAllProducts.length === 0 ? (
            <p className="text-gray-400 dark:text-gray-500 text-center py-10 text-sm">
              {searchQuery ? `No products found for "${searchQuery}"` : "No products available"}
            </p>
          ) : (
            <div className="relative group">
              <button
                onClick={() => scrollContainer(allScrollRef, "left")}
                className={`absolute left-[-20px] top-[45%] -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-12 h-12 rounded-full shadow-lg border text-gray-700 dark:text-gray-200 transition-all hover:scale-105 ${canScrollAllLeft ? "opacity-0 group-hover:opacity-100 cursor-pointer" : "opacity-0 pointer-events-none"}`}
                style={{ background: "var(--surface-elevated)", borderColor: "var(--surface-border)" }}
                aria-label="Scroll left"
              >
                <ChevronLeft size={24} />
              </button>

              <div
                ref={allScrollRef}
                onScroll={checkScrollAll}
                className="flex overflow-x-auto pb-6 gap-5 snap-x scrollbar-hide scroll-smooth"
              >
                {filteredAllProducts.map((product) => (
                  <div key={product._id || product.id} className="w-full sm:w-[280px] lg:w-[290px] shrink-0 snap-center">
                    <ProductCard product={product} onAddToCart={handleAddToCart} />
                  </div>
                ))}
              </div>

              <button
                onClick={() => scrollContainer(allScrollRef, "right")}
                className={`absolute right-[-20px] top-[45%] -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-12 h-12 rounded-full shadow-lg border text-gray-700 dark:text-gray-200 transition-all hover:scale-105 ${canScrollAllRight ? "opacity-0 group-hover:opacity-100 cursor-pointer" : "opacity-0 pointer-events-none"}`}
                style={{ background: "var(--surface-elevated)", borderColor: "var(--surface-border)" }}
                aria-label="Scroll right"
              >
                <ChevronRight size={24} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Best Deals on a Budget */}
      <BestDeals 
        bestDealsLoading={bestDealsLoading} 
        bestDeals={bestDeals} 
        isVisible={bestDealsVisible}
        navigate={navigate} 
      />
 {/* Hot Deals */}
      <HotDealSection />
      {/* Why Choose Us */}
      <WhyChooseUs />

      {/* Watch and Shop */}
      <WatchAndShop />

      {/* Testimonials */}
      <TestimonialSection />

      {/* Blogs */}
      <BlogSection />

      {/* FAQ Section */}
      <FaqSection />

      <style>{`
        @keyframes scroll-left {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll-left {
          animation: scroll-left 60s linear infinite;
        }
        @keyframes sparkle-float {
          0%, 100% { transform: translateY(0px) scale(1);   opacity: 0.7; }
          50%       { transform: translateY(-8px) scale(1.4); opacity: 1; }
        }
        @keyframes float-image {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-10px); }
        }
        @keyframes deal-ping {
          0%   { transform: scale(1);   opacity: 0.7; }
          75%, 100% { transform: scale(1.8); opacity: 0; }
        }
        @keyframes ripple-burst {
          0% { transform: translate(-50%, -50%) scale(0); opacity: 1; border: 2px solid white; }
          100% { transform: translate(-50%, -50%) scale(25); opacity: 0; border: 0px solid white; }
        }
        @keyframes click-flash {
          0% { opacity: 0; }
          40% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes card-cycle-1 {
          0%       { transform: translateX(0) translateY(0) scale(1)    rotateZ(0deg);   opacity: 1; z-index: 4; }
          20%      { transform: translateX(0) translateY(0) scale(1)    rotateZ(0deg);   opacity: 1; z-index: 4; }
          35%      { transform: translateX(180px) translateY(-20px) scale(0.9) rotateZ(8deg);  opacity: 0; z-index: 4; }
          36%      { transform: translateX(-30px) translateY(60px) scale(0.82) rotateZ(0deg); opacity: 0; z-index: 0; }
          55%      { transform: translateX(0) translateY(48px) scale(0.82) rotateZ(0deg); opacity: 0.5; z-index: 0; }
          80%      { transform: translateX(0) translateY(48px) scale(0.82) rotateZ(0deg); opacity: 0.7; z-index: 0; }
          100%     { transform: translateX(0) translateY(0) scale(1)    rotateZ(0deg);   opacity: 1; z-index: 4; }
        }
        @keyframes card-cycle-2 {
          0%       { transform: translateX(0) translateY(16px) scale(0.94); z-index: 3; opacity: 0.9; }
          20%      { transform: translateX(0) translateY(16px) scale(0.94); z-index: 3; opacity: 0.9; }
          55%      { transform: translateX(0) translateY(0)    scale(1);    z-index: 3; opacity: 1; }
          80%      { transform: translateX(0) translateY(0)    scale(1);    z-index: 3; opacity: 1; }
          100%     { transform: translateX(0) translateY(16px) scale(0.94); z-index: 3; opacity: 0.9; }
        }
        @keyframes card-cycle-3 {
          0%       { transform: translateX(0) translateY(32px) scale(0.88); z-index: 2; opacity: 0.75; }
          20%      { transform: translateX(0) translateY(32px) scale(0.88); z-index: 2; opacity: 0.75; }
          55%      { transform: translateX(0) translateY(16px) scale(0.94); z-index: 2; opacity: 0.9; }
          80%      { transform: translateX(0) translateY(16px) scale(0.94); z-index: 2; opacity: 0.9; }
          100%     { transform: translateX(0) translateY(32px) scale(0.88); z-index: 2; opacity: 0.75; }
        }
        @keyframes card-cycle-4 {
          0%       { transform: translateX(0) translateY(48px) scale(0.82); z-index: 1; opacity: 0.6; }
          20%      { transform: translateX(0) translateY(48px) scale(0.82); z-index: 1; opacity: 0.6; }
          55%      { transform: translateX(0) translateY(32px) scale(0.88); z-index: 1; opacity: 0.75; }
          80%      { transform: translateX(0) translateY(32px) scale(0.88); z-index: 1; opacity: 0.75; }
          100%     { transform: translateX(0) translateY(48px) scale(0.82); z-index: 1; opacity: 0.6; }
        }
        @keyframes swipe-hint {
          0%, 100% { transform: translateX(-50%) translateX(0); opacity: 0.4; }
          50%       { transform: translateX(-50%) translateX(5px); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default Home;
